import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Github,
  ExternalLink,
  Calendar,
  Users,
  Clock,
  Star,
  GitFork,
  TrendingUp,
  Mail,
  MessageCircle,
} from "lucide-react";
import TechBadge from "./TechBadge";

const ProjectModal = ({ project, isOpen, onClose }) => {
  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!project) return null;

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: 50,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: {
        duration: 0.2,
      },
    },
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "in-progress":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      case "maintenance":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200";
      case "planned":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={onClose}
        >
          <motion.div
            className="relative w-full max-w-4xl max-h-[90vh] bg-white dark:bg-dark-800 rounded-2xl shadow-2xl overflow-hidden"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <motion.button
              className="absolute top-4 right-4 z-10 p-2 bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              onClick={onClose}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <X size={20} />
            </motion.button>

            {/* Scrollable Content */}
            <div className="max-h-[90vh] overflow-y-auto">
              {/* Hero Image */}
              <div className="relative h-64 md:h-80 overflow-hidden">
                <img
                  src={project.image || "/api/placeholder/800/400"}
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/60" />

                {/* Status and Featured Badges */}
                <div className="absolute top-4 left-4 flex space-x-2">
                  {project.featured && (
                    <span className="bg-primary-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      Featured
                    </span>
                  )}
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(
                      project.status
                    )}`}
                  >
                    {project.status.charAt(0).toUpperCase() +
                      project.status.slice(1).replace("-", " ")}
                  </span>
                </div>

                {/* Title Overlay */}
                <div className="absolute bottom-6 left-6 right-16">
                  <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-2">
                    {project.title}
                  </h2>
                  <p className="text-gray-200 text-lg">{project.description}</p>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 md:p-8">
                {/* Project Meta Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                    <Calendar size={16} className="mr-2 text-primary-500" />
                    <div>
                      <div className="text-xs uppercase tracking-wide">
                        Duration
                      </div>
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {project.duration}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                    <Users size={16} className="mr-2 text-primary-500" />
                    <div>
                      <div className="text-xs uppercase tracking-wide">
                        Team Size
                      </div>
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {project.teamSize}{" "}
                        {project.teamSize === "1" ? "Person" : "People"}
                      </div>
                    </div>
                  </div>

                  {project.completedDate && (
                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                      <Clock size={16} className="mr-2 text-primary-500" />
                      <div>
                        <div className="text-xs uppercase tracking-wide">
                          Completed
                        </div>
                        <div className="font-semibold text-gray-900 dark:text-white">
                          {new Date(project.completedDate).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  )}

                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-4 mb-8">
                  {project.githubUrl && (
                    <motion.a
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-6 py-3 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 font-semibold rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Github size={20} className="mr-2" />
                      View Source
                    </motion.a>
                  )}

                  {project.liveUrl && (
                    <motion.a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-6 py-3 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors"
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <ExternalLink size={20} className="mr-2" />
                      Live Demo
                    </motion.a>
                  )}
                </div>

                {/* Description */}
                <div className="mb-8">
                  <h3 className="text-xl font-heading font-semibold text-gray-900 dark:text-white mb-4">
                    Project Overview
                  </h3>
                  <div className="prose dark:prose-invert max-w-none">
                    {project.longDescription
                      .split("\n")
                      .map((paragraph, index) => (
                        <p
                          key={index}
                          className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4"
                        >
                          {paragraph.trim()}
                        </p>
                      ))}
                  </div>
                </div>

                {/* Technologies */}
                <div className="mb-8">
                  <h3 className="text-xl font-heading font-semibold text-gray-900 dark:text-white mb-4">
                    Technologies Used
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech, index) => (
                      <TechBadge key={index} tech={tech} variant="colored" />
                    ))}
                  </div>
                </div>

                {/* Challenges & Solutions */}
                {project.challenges && project.solutions && (
                  <div className="grid md:grid-cols-2 gap-8 mb-8">
                    <div>
                      <h3 className="text-xl font-heading font-semibold text-gray-900 dark:text-white mb-4">
                        Key Challenges
                      </h3>
                      <ul className="space-y-3">
                        {project.challenges.map((challenge, index) => (
                          <li key={index} className="flex items-start">
                            <div className="w-2 h-2 bg-red-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                            <span className="text-gray-600 dark:text-gray-400">
                              {challenge}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-heading font-semibold text-gray-900 dark:text-white mb-4">
                        Solutions Implemented
                      </h3>
                      <ul className="space-y-3">
                        {project.solutions.map((solution, index) => (
                          <li key={index} className="flex items-start">
                            <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                            <span className="text-gray-600 dark:text-gray-400">
                              {solution}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Investor / Partner Section */}
                {project.seekingInvestment && (
                  <div className="mb-8 rounded-2xl overflow-hidden border border-emerald-200 dark:border-emerald-800/50">
                    <div className="bg-emerald-500 px-6 py-4 flex items-center gap-3">
                      <TrendingUp size={20} className="text-white" />
                      <h3 className="text-lg font-bold text-white">Seeking Investment & Partners</h3>
                    </div>
                    <div className="bg-emerald-50 dark:bg-emerald-900/10 px-6 py-5">
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
                        <strong>Klarogh is actively looking for:</strong>
                      </p>
                      <ul className="space-y-1.5 mb-5 text-gray-600 dark:text-gray-400 text-sm">
                        <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" /> Early-stage investors who believe in making legal documents accessible to everyone in Africa and beyond</li>
                        <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" /> Co-founders with legal, AI, or business development backgrounds</li>
                        <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" /> Strategic partners — music labels, law firms, creator platforms</li>
                        <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" /> Anyone who can help push this forward — advisors, connectors, believers</li>
                      </ul>
                      <p className="text-gray-500 dark:text-gray-400 text-sm italic mb-5">
                        "If you see the vision, let's talk. No idea is too early to explore."
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <a
                          href="mailto:emmanuelboye1957@gmail.com?subject=Klarogh Investment Interest"
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-colors text-sm"
                        >
                          <Mail size={15} /> Email Me
                        </a>
                        <a
                          href="https://wa.me/233542510400?text=Hi%20Emmanuel%2C%20I%27m%20interested%20in%20Klaroafrica%20(klaro-africa.netlify.app)"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#25D366] hover:bg-[#20b858] text-white font-semibold rounded-xl transition-colors text-sm"
                        >
                          <MessageCircle size={15} /> WhatsApp
                        </a>
                        <a
                          href="tel:+233593501488"
                          className="inline-flex items-center gap-2 px-5 py-2.5 border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 font-semibold rounded-xl transition-colors text-sm"
                        >
                          Call Me
                        </a>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;
